<template>
  <div>
    <v-card
      flat
      class="rounded-lg mt-5 textLight--text px-6 py-3"
      :color="backgroundColor"
      style="min-width: 100%; max-width: fit-content; width: fit-content"
    >
      <div class="text-center d-flex align-center">
        <div class="text-ellipse-2 text-start pe-3" style="width: 250px">
          {{ getLocalisedValue(batchData.organizationName) }}
        </div>

        <div class="text-ellipse-2 text-start pe-3" style="width: 150px">
          {{ batchData.UID }}
        </div>

        <div class="text-ellipse-2 text-start pe-3" style="width: 250px">
          {{ batchData.trainerName }}
        </div>

        <div class="text-ellipse-2 text-start pe-3" style="width: 250px">
          {{ getLocalisedValue(batchData.trainingTitle) }}
        </div>

        <div class="text-center pe-3" style="width: 150px">
          {{ batchData.employees.length }}
        </div>

        <div class="text-start pe-3" style="width: 120px">
          {{
            batchData.startDate
              ? moment(parseInt(batchData.startDate)).format("DD MMM YYYY")
              : "-"
          }}
        </div>

        <div
          class="d-flex justify-start align-center pe-3"
          style="width: 150px"
        >
          <span :class="getColor(batchData.purchaseInfo)">
            {{ getInfoStatus(batchData.purchaseInfo) }}
          </span>

          <v-menu v-if="canUpdate(batchData.purchaseInfo)">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                v-bind="attrs"
                v-on="on"
                :loading="isPurchaseInfoStatusUpdating"
              >
                <v-icon>mdi-chevron-down</v-icon>
              </v-btn>
            </template>

            <v-list>
              <v-list-item
                @click="updatePurchaseInfo(item)"
                v-for="(item, i) in statuses"
                :key="i"
              >
                <v-list-item-title>{{ item }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>

          <v-btn v-else icon disabled style="visibility: hidden">
            <v-icon>mdi-chevron-down</v-icon>
          </v-btn>
        </div>

        <div
          class="d-flex justify-start align-center pe-3"
          style="width: 180px"
        >
          <span :class="getColor(batchData.certificateInfo)">
            {{ getInfoStatus(batchData.certificateInfo) }}
          </span>

          <v-menu v-if="canUpdate(batchData.certificateInfo)">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                v-bind="attrs"
                v-on="on"
                :loading="isCertificateInfoStatusUpdating"
              >
                <v-icon>mdi-chevron-down</v-icon>
              </v-btn>
            </template>

            <v-list>
              <v-list-item
                @click="updateCertificateInfo(item)"
                v-for="(item, i) in statuses"
                :key="i"
              >
                <v-list-item-title>{{ item }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>

          <v-btn v-else icon disabled style="visibility: hidden">
            <v-icon>mdi-chevron-down</v-icon>
          </v-btn>
        </div>

        <div
          class="d-flex justify-start align-center pe-3"
          style="width: 180px"
        >
          <span :class="getColor(batchData.invoiceInfo)">
            {{ getInfoStatus(batchData.invoiceInfo) }}
          </span>

          <v-menu v-if="canUpdate(batchData.invoiceInfo)">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                v-bind="attrs"
                v-on="on"
                :loading="isInvoiceInfoStatusUpdating"
              >
                <v-icon>mdi-chevron-down</v-icon>
              </v-btn>
            </template>

            <v-list>
              <v-list-item
                @click="updateInvoiceInfo(item)"
                v-for="(item, i) in statuses"
                :key="i"
              >
                <v-list-item-title>{{ item }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>

          <v-btn v-else icon disabled style="visibility: hidden">
            <v-icon>mdi-chevron-down</v-icon>
          </v-btn>
        </div>

        <div
          class="d-flex justify-start align-center pe-3"
          style="width: 150px"
        >
          <span :class="getColor(batchData.paymentInfo)">
            {{ getInfoStatus(batchData.paymentInfo) }}
          </span>

          <v-menu v-if="canUpdate(batchData.paymentInfo)">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                v-bind="attrs"
                v-on="on"
                :loading="isPaymentInfoStatusUpdating"
              >
                <v-icon>mdi-chevron-down</v-icon>
              </v-btn>
            </template>

            <v-list>
              <v-list-item
                @click="updatePaymentInfo(item)"
                v-for="(item, i) in statuses"
                :key="i"
              >
                <v-list-item-title>{{ item }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>

          <v-btn v-else icon disabled style="visibility: hidden">
            <v-icon>mdi-chevron-down</v-icon>
          </v-btn>
        </div>

        <div>
          <v-btn
            v-if="havePermissionDeleteBatch"
            icon
            depressed
            color="error"
            @click.stop="deleteBatch"
          >
            <v-icon> mdi-trash-can-outline </v-icon>
          </v-btn>

          <v-btn v-else icon depressed color="error" style="visibility: hidden">
            <v-icon> mdi-trash-can-outline </v-icon>
          </v-btn>
        </div>
      </div>
    </v-card>
  </div>
</template>

<script>
import { hasPermission } from "@/app/user-module/helpers/authHelper";
import {
  deleteBatch,
  updateBatch,
} from "@/app/batch-module/methods/updateOrDeleteBatch";

export default {
  name: "BatchReportCard",
  props: {
    batchData: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      statuses: ["COMPLETED"],
      showPurchaseInfoMenu: false,
      showCertificateInfoMenu: false,
      showInvoiceInfoMenu: false,
      showPaymentInfoMenu: false,
      isPurchaseInfoStatusUpdating: false,
      isCertificateInfoStatusUpdating: false,
      isInvoiceInfoStatusUpdating: false,
      isPaymentInfoStatusUpdating: false,
    };
  },
  computed: {
    isPartiallyCompleted() {
      return (
        this.batchData.purchaseInfo?.status === "COMPLETED" &&
        this.batchData.certificateInfo?.status === "COMPLETED" &&
        this.batchData.invoiceInfo?.status === "COMPLETED"
      );
    },
    isCompleted() {
      return (
        this.isPartiallyCompleted &&
        this.batchData.paymentInfo?.status === "COMPLETED"
      );
    },
    backgroundColor() {
      const pendingColor = "rgba(245,124,0,0.09)";
      const partiallyCompletedColor = "rgba(142,175,76,0.1)";
      const completedColor = "rgba(76,175,80,0.1)";

      return this.isCompleted
        ? completedColor
        : this.isPartiallyCompleted
        ? partiallyCompletedColor
        : pendingColor;
    },
    havePermissionUpdateBatch() {
      return hasPermission({
        currentRole: this.$store.state.authenticatedUserRole,
        currentPermissions: this.$store.state.authenticatedUserPermissions,
        requiredPermission: ["UPDATE_BATCH"],
        restrictOrganizationManager:
          this.$store.state.authenticatedUser?.isOrganizationManager,
      });
    },
    havePermissionDeleteBatch() {
      return hasPermission({
        currentRole: this.$store.state.authenticatedUserRole,
        currentPermissions: this.$store.state.authenticatedUserPermissions,
        requiredPermission: ["DELETE_BATCH"],
        restrictOrganizationManager:
          this.$store.state.authenticatedUser?.isOrganizationManager,
      });
    },
  },
  methods: {
    getColor(info) {
      return info.status === "PENDING"
        ? "orange--text"
        : info.status === "COMPLETED"
        ? "green--text"
        : "";
    },
    getInfoStatus(info) {
      return info.status
        ? info.status.charAt(0).toUpperCase() +
            info.status.slice(1).toLowerCase()
        : "-";
    },
    canUpdate(info) {
      return this.havePermissionUpdateBatch && info.status !== "COMPLETED";
    },
    async updatePurchaseInfo(status) {
      this.isPurchaseInfoStatusUpdating = true;

      await updateBatch({
        id: this.batchData._id,
        input: { purchaseInfo: { status } },
      }).then(_ => {
        this.isPurchaseInfoStatusUpdating = false;
      });
    },
    async updateCertificateInfo(status) {
      this.isCertificateInfoStatusUpdating = true;

      await updateBatch({
        id: this.batchData._id,
        input: { certificateInfo: { status } },
      }).then(_ => {
        this.isCertificateInfoStatusUpdating = false;
      });
    },
    async updateInvoiceInfo(status) {
      this.isInvoiceInfoStatusUpdating = true;

      await updateBatch({
        id: this.batchData._id,
        input: { invoiceInfo: { status } },
      }).then(_ => {
        this.isInvoiceInfoStatusUpdating = false;
      });
    },
    async updatePaymentInfo(status) {
      this.isPaymentInfoStatusUpdating = true;

      await updateBatch({
        id: this.batchData._id,
        input: { paymentInfo: { status } },
      }).then(_ => {
        this.isPaymentInfoStatusUpdating = false;
      });
    },
    deleteBatch() {
      this.$dialog
        .confirm(this.$t("batch.confirmDelete"), { loader: true })
        .then(dialog => {
          deleteBatch(this.batchData._id).then(res => {
            if (res.data) this.$parent.refetch();
            if (res.error) this.$toast.error("Some error occurred!");
            dialog?.close();
          });
        })
        .catch(_ => {});
    },
  },
};
</script>
